/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { oc } from 'ts-optchain';
import clsx from 'clsx';

import { generateAriaProps, getErrorDisplayText } from './inputHelpers';
import PureBaseComponent from '../components/PureBaseComponent';
import TextInputAffix from '../components/inputs/TextInputAffix';

export interface TextInputProps {
  'data-testid'?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  error?: string | object;
  hasError?: boolean;
  hint?: string;
  id?: string;
  innerRef?: React.Ref<any>;
  label?: string;
  name?: string;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
  placeholder?: string;
  postfix?: JSX.Element | string;
  prefix?: JSX.Element | string;
  readOnly?: boolean;
  role?: string;
  type?: string;
  value?: string;
  warn?: string;
}

interface State {
  isFocused: boolean;
}

/**
 * @deprecated
 * Plase see ui folder of your project
 * Use `TextInput` for a basic text field
 *
 * ```js
 * import { TextInput } from '.';
 * ```
 */
export class TextInputComponent extends PureBaseComponent<TextInputProps, State> {
  state = {
    isFocused: false,
  };

  handleFocus(evt: React.FocusEvent<HTMLInputElement>) {
    const { onFocus } = this.props;
    this.setState((prevState: State) => ({
      isFocused: true,
    }));
    if (onFocus) {
      onFocus(evt);
    }
  }

  handleBlur(evt: React.FocusEvent<HTMLInputElement>) {
    const { onBlur } = this.props;

    this.setState((prevState: State) => ({
      isFocused: false,
    }));
    if (onBlur) {
      onBlur(evt);
    }
  }

  render() {
    const {
      label,
      error,
      hint,
      hasError,
      innerRef,
      className,
      prefix,
      postfix,
      onFocus,
      onBlur,
      warn,
      ...restProps
    } = this.props;

    const { isFocused } = this.state;

    let decoratedPrefix: React.ReactNode;
    let decoratedPostfix: React.ReactNode;

    const ariaProps = generateAriaProps({
      hasError,
      label,
      placeholder: restProps.placeholder,
      name: restProps.name,
      hint,
    });

    if (prefix) {
      if (typeof prefix === 'string') {
        decoratedPrefix = (
          <TextInputAffix text={prefix} className="TextInput-prefix" key="TextInput-prefix" />
        );
      } else {
        const prefixClassName: string = (oc(prefix) as any).props.className('');
        decoratedPrefix = React.cloneElement(prefix, {
          className: clsx('TextInput-prefix', prefixClassName),
        });
      }
    }

    if (postfix) {
      if (typeof postfix === 'string') {
        decoratedPostfix = (
          <TextInputAffix text={postfix} className="TextInput-postfix" key="TextInput-postfix" />
        );
      } else {
        const postfixClassName: string = (oc(postfix) as any).props.className('');
        decoratedPostfix = React.cloneElement(postfix, {
          className: clsx('TextInput-postfix', postfixClassName),
        });
      }
    }

    return (
      <div className={clsx('Field', className)}>
        {label && (
          <label
            htmlFor={restProps.name || restProps.id}
            className={clsx('Label', { 'Label--is-error': hasError })}
          >
            {label}
          </label>
        )}
        <div
          className={clsx('InputContainer TextInput TextInput--default', {
            'TextInput--is-error': hasError,
            'has-prefix': !!prefix,
            'has-postfix': !!postfix,
            'TextInput--is-focused': isFocused,
          })}
        >
          {decoratedPrefix}
          <input
            ref={innerRef}
            className={clsx('Input TextInput-input', {
              'has-prefix': !!prefix,
              'has-postfix': !!postfix,
              'has-errors': hasError,
            })}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            type="text"
            {...ariaProps}
            {...restProps}
          />
          {decoratedPostfix}
        </div>

        {hint && (
          <div className="Hints" id={`${restProps.name}-hint`}>
            <div className="Hint">{hint}</div>
          </div>
        )}

        {warn && (
          <div className="Warnings">
            <div className="Warning">{warn}</div>
          </div>
        )}

        {hasError && (
          <div className="Errors" id={`${restProps.name}-error`}>
            <div className="Error">{getErrorDisplayText(error)}</div>
          </div>
        )}
      </div>
    );
  }
}

/**
 * @deprecated
 */
export const TextInput = React.forwardRef<any, TextInputProps>((props, ref) => (
  <TextInputComponent innerRef={ref} {...props} />
));
